import { apiFetch } from '@/lib/api';
import type { FeedbackTicket, FeedbackMessage, TicketDetail } from './feedbackService';

export interface TicketWithUser extends FeedbackTicket {
  user_email: string;
  user_name: string | null;
  org_name: string | null;
}

export interface FeedbackStats {
  total: number;
  open: number;
  [key: string]: number;
}

export interface FeedbackSettings {
  notification_emails: string[];
  ai_system_prompt: string;
  [key: string]: any;
}

export const adminFeedbackService = {
  async listTickets(filters?: {
    type?: string;
    status?: string;
    organization_id?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: TicketWithUser[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.type) params.set('type', filters.type);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.organization_id) params.set('organization_id', filters.organization_id);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.pageSize) params.set('pageSize', String(filters.pageSize));
    const res = await apiFetch(`/api/admin/feedback/tickets?${params}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to list tickets');
    return json;
  },

  async getTicket(id: string): Promise<TicketDetail> {
    const res = await apiFetch(`/api/admin/feedback/tickets/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get ticket');
    return json.data;
  },

  async reply(ticketId: string, body: string): Promise<FeedbackMessage> {
    const res = await apiFetch(`/api/admin/feedback/tickets/${ticketId}/reply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to send reply');
    return json.data;
  },

  async updateStatus(ticketId: string, status: string): Promise<void> {
    const res = await apiFetch(`/api/admin/feedback/tickets/${ticketId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || 'Failed to update status');
    }
  },

  async getStats(): Promise<FeedbackStats> {
    const res = await apiFetch('/api/admin/feedback/stats');
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get stats');
    return json.data;
  },

  async getSettings(): Promise<FeedbackSettings> {
    const res = await apiFetch('/api/admin/feedback/settings');
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to get settings');
    return json.data;
  },

  async updateSetting(key: string, value: any): Promise<void> {
    const res = await apiFetch(`/api/admin/feedback/settings/${key}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) {
      const json = await res.json();
      throw new Error(json.error || 'Failed to update setting');
    }
  },
};
